import { useEffect, useMemo, useState } from "react";

import { IS_PROD_BUILD } from "@/constants/env";
import { Routes as AppRoutes } from "@/constants/routes";
import { ConsentCategories, CookieYesConsent, CookieYesConsentUpdate } from "@/types/common";
import { AmplitudeTracker, PaltaDataPlatformTracker, Sentry, TagManager } from "@/utils/trackers";

const IS_DEV = !IS_PROD_BUILD;

enum TrackersTypes {
    advertisement = "advertisement",
    analytics = "analytics",
    functional = "functional",
}

const trackersByType = {
    [TrackersTypes.advertisement]: [TagManager],
    [TrackersTypes.analytics]: [AmplitudeTracker, PaltaDataPlatformTracker],
    [TrackersTypes.functional]: [Sentry],
};

const initTrackers = (type: TrackersTypes) => {
    trackersByType[type].forEach((tracker) => tracker.init());
};

const stopTrackers = (type: TrackersTypes) => {
    trackersByType[type].forEach((tracker) => tracker.stop());
};

const categoriesFromUpdate = ({ accepted }: CookieYesConsentUpdate): ConsentCategories =>
    Object.fromEntries(accepted.map((category) => [category, true]));

const useConsent = () => {
    const [categories, setCategories] = useState<ConsentCategories | null>(
        () => window.getCkyConsent?.().categories ?? null
    );

    const isFunnelFox = useMemo(() => window.location.pathname.includes(AppRoutes.FFPaywall), []);

    useEffect(() => {
        if (IS_DEV || isFunnelFox) {
            Object.values(TrackersTypes).forEach(initTrackers);
            return;
        }

        if (!categories) return;

        Object.values(TrackersTypes).forEach((type) => {
            categories[type] ? initTrackers(type) : stopTrackers(type);
        });
    }, [categories, isFunnelFox]);

    useEffect(() => {
        const onBannerLoad = (event: Event) => {
            const consent = (event as CustomEvent<CookieYesConsent>).detail;
            setCategories({ ...consent.categories });
        };

        const onConsentUpdate = (event: Event) => {
            const update = (event as CustomEvent<CookieYesConsentUpdate>).detail;
            setCategories(categoriesFromUpdate(update));
        };

        document.addEventListener("cookieyes_banner_load", onBannerLoad);
        document.addEventListener("cookieyes_consent_update", onConsentUpdate);

        return () => {
            document.removeEventListener("cookieyes_banner_load", onBannerLoad);
            document.removeEventListener("cookieyes_consent_update", onConsentUpdate);
        };
    }, []);
};

export default useConsent;
